<template>
  <modal-wrapper
    ref="modalRef"
    :busy="isAddingSupplier"
    :content-overflow-y="'visible'"
    :header="t('projectsSuppliers.addSupplier')"
    :ok-button-label="t('generic.add')"
    @ok-button-click="addSelectedSupplier"
  >
    <styled-multiselect
      v-model="selectedSupplier"
      :options="suppliers"
      track-by="name"
      :label="label"
      :placeholder="t('projectsSuppliers.enterSupplierName')"
      :loading="isLoadingSuppliers"
      @search-change="debouncedQuerySuppliers"
    />

    <div v-if="isConflictError" class="error">
      {{ t('projectsSuppliers.errors.supplierAlreadyAdded') }}
    </div>
  </modal-wrapper>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { debounce } from 'lodash';
  import axios, { AxiosError, AxiosResponse } from 'axios';
  import endpointsList from '../../helpers/endpointsList';
  import { ISupplier } from 'types';
  import { useModalWrapper } from '../ModalWrapper';
  import ModalWrapper from '../ModalWrapper/ModalWrapper.vue';

  const { t } = useI18n();

  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  const props = defineProps<{ projectId: string }>();
  const emit = defineEmits<{ (e: 'supplier-added'): void }>();

  const suppliers = ref<ISupplier[]>([]);
  const selectedSupplier = ref<ISupplier | undefined>(undefined);
  const isLoadingSuppliers = ref(false);
  const isConflictError = ref(false);
  const isAddingSupplier = ref(false);

  async function querySuppliers(query: string) {
    if (query.length < 2) {
      return;
    }
    isLoadingSuppliers.value = true;

    try {
      const response: AxiosResponse<ISupplier[]> = await axios.get(
        endpointsList.suppliers.searchSuppliers({
          name: query,
          SAPNumber: '',
        }),
      );

      suppliers.value = response.data;
    } catch {
      //
    }
    isLoadingSuppliers.value = false;
  }

  const debouncedQuerySuppliers = debounce(querySuppliers, 800);

  async function addSelectedSupplier() {
    if (!selectedSupplier.value) {
      return;
    }
    isAddingSupplier.value = true;
    isConflictError.value = false;

    try {
      await axios.patch(
        endpointsList.projects.addSupplierToProject(props.projectId),
        {
          supplierId: selectedSupplier.value._id,
        },
      );

      emit('supplier-added');
      cleanModal();
      modalRef.value?.hide();
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        if (err.response?.status === 409) {
          isConflictError.value = true;
        }
      }
    }

    isAddingSupplier.value = false;
  }

  function cleanModal() {
    selectedSupplier.value = undefined;
    suppliers.value = [];
  }

  function label({ name, SAPNumber }: { name: string; SAPNumber: string }) {
    if (SAPNumber) {
      return `${name} — [${SAPNumber}]`;
    }

    return `${name}`;
  }
</script>

<style scoped lang="scss">
  .error {
    color: red;
    text-align: center;
    margin-top: 20px;
  }
</style>
